import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { AlertTriangle } from '@recruit-robin/robin-theme/web-icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { useDictionary } from '@/shared/lib/i18n';

type Props = {
  creditBalance: number;
};

export const IndeedCreditsSyncPill = ({ creditBalance }: Props) => {
  const dictionary = useDictionary();
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} style={{ display: 'inline-flex' }}>
        <Pill
          label={creditBalance === 0 ? dictionary.indeedCreditsUnknown : `${creditBalance} ${dictionary.credits}`}
          style="icon-removable"
          type="warning"
          startIcon={<AlertTriangle size={14} />}
          padding="text"
          size="small"
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <FloatingPortal>
            <motion.div
              ref={refs.setFloating}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ ...floatingStyles, zIndex: 1000 }}
              {...getFloatingProps()}>
              <div
                style={{
                  background: '#2D2D2D',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  maxWidth: '260px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                }}>
                <Typography variant={(v) => v.heading[6]} color={(c) => c.neutral.variant[0]}>
                  {dictionary.indeedCreditsOutOfSync}
                </Typography>
                <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[30]}>
                  {dictionary.indeedCreditsOutOfSyncDescription}
                </Typography>
                <a
                  href="https://resumes.indeed.com/admin"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#4088F5', textDecoration: 'none', fontSize: '12px' }}>
                  {dictionary.indeedCheckBalance} ↗
                </a>
              </div>
            </motion.div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
};
